import { Link, Navigate } from "@tanstack/react-router";
import type {
  ApplicationDraftSteps,
  BuyerAddressView,
  FinancialFact,
  MeResponse,
  TexCaseView,
  WorkQueueItem,
  WorkQueueResponse,
} from "@tex/finance-shared";
import { CaseActivityTab } from "../components/case-activity";
import { ClosingTab } from "../components/closing-tab";
import { DocumentsCard } from "../components/documents-card";
import { EligibilityCard } from "../components/eligibility-card";
import { TexInformationRequestsCard } from "../components/information-requests";
import { LendersTab } from "../components/lenders-tab";
import { ReviewWorkspace } from "../components/review-workspace";
import { Alert, Card, Mono, Row, StageBadge } from "../components/ui";
import { errorMessage } from "../lib/api";
import {
  FINANCIAL_PERIOD_LABELS,
  FINANCIAL_SOURCE_LABELS,
} from "../lib/application-form";
import { ENTITY_TYPE_LABELS, SOFT_COST_LABELS } from "../lib/case-form";
import { useCase } from "../lib/cases";
import { formatAmount, formatDate, formatDay, humanize } from "../lib/format";
import { useReview, useWorkQueue } from "../lib/reviews";
import { isTexStaff, useMe } from "../lib/session";
import { DiscrepancyList } from "./application";
import { AssignControl, NextActionBadge } from "./tex-queue";

export const TEX_CASE_TABS = [
  "application",
  "assets",
  "documents",
  "review",
  "lenders",
  "closing",
  "activity",
] as const;
export type TexCaseTab = (typeof TEX_CASE_TABS)[number];

const TAB_LABELS: Record<TexCaseTab, string> = {
  application: "Application",
  assets: "Assets",
  documents: "Documents",
  review: "Review",
  lenders: "Lenders",
  closing: "Closing",
  activity: "Activity",
};

export function TexCasePage({
  caseId,
  tab,
  onTab,
}: {
  caseId: string;
  tab: TexCaseTab;
  onTab: (t: TexCaseTab) => void;
}) {
  const me = useMe();
  const staff = isTexStaff(me.data);
  const kase = useCase(caseId);
  const review = useReview(caseId, staff);
  const queue = useWorkQueue("all", staff);

  if (me.data === null) return <Navigate to="/sign-in" />;
  if (!me.data || kase.isPending)
    return <p className="text-sm text-neutral-500">Loading…</p>;
  if (!staff)
    return (
      <Alert tone="error">The underwriting desk is for Tex staff only.</Alert>
    );
  if (kase.isError)
    return <Alert tone="error">{errorMessage(kase.error)}</Alert>;
  if (kase.data.audience !== "tex")
    return (
      <Alert tone="error">
        This view is only rendered from the Tex projection.
      </Alert>
    );
  const view = kase.data;
  const queued = queue.data?.items.find((i) => i.case_id === caseId) ?? null;
  const reviewers = queue.data?.reviewers ?? [];
  const canWrite = view.stage === "review";

  return (
    <section className="space-y-6">
      <Header me={me.data} view={view} queued={queued} reviewers={reviewers} />

      <div
        role="tablist"
        className="flex gap-1 border-b border-[var(--border-subtle)] text-sm"
      >
        {TEX_CASE_TABS.map((t) => (
          <button
            key={t}
            role="tab"
            type="button"
            aria-selected={tab === t}
            onClick={() => onTab(t)}
            className={`-mb-px border-b-2 px-3 py-2 ${tab === t ? "border-tex-blue font-medium text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}
          >
            {TAB_LABELS[t]}
            {t === "review" && review.data?.review && (
              <span className="ml-1 text-xs text-neutral-400">
                v{review.data.review.version}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "application" && <ApplicationTab view={view} />}
      {tab === "assets" && <AssetsTab view={view} />}
      {tab === "documents" && (
        <DocumentsCard caseId={caseId} audience="tex" canUpload />
      )}
      {tab === "review" && (
        <div className="space-y-6">
          {view.stage !== "review" && (
            <Alert tone="info">
              This case is in {humanize(view.stage)}; the review is read-only
              here.{" "}
              {view.stage === "application" || view.stage === "lead"
                ? "The borrower has not submitted yet."
                : ""}
            </Alert>
          )}
          {review.isError && (
            <Alert tone="error">{errorMessage(review.error)}</Alert>
          )}
          {review.data && (
            <ReviewWorkspace
              caseId={caseId}
              data={review.data}
              canWrite={canWrite}
            />
          )}
          <TexInformationRequestsCard
            caseId={caseId}
            canWrite={
              canWrite ||
              view.stage === "lender_review" ||
              view.stage === "offer_review" ||
              view.stage === "closing"
            }
          />
        </div>
      )}
      {tab === "lenders" && <LendersTab caseId={caseId} view={view} />}
      {tab === "closing" && (
        <ClosingTab caseId={caseId} opsAdmin={me.data.tex_role === "ops_admin"} />
      )}
      {tab === "activity" && <CaseActivityTab view={view} />}

      <p className="text-xs text-neutral-400">
        Case <Mono>{view.id}</Mono> · updated {formatDate(view.updated_at)} ·{" "}
        <Link
          to="/cases/$caseId"
          params={{ caseId }}
          className="hover:underline"
        >
          dealer-facing record
        </Link>
      </p>
    </section>
  );
}

function Header({
  me,
  view,
  queued,
  reviewers,
}: {
  me: MeResponse;
  view: TexCaseView;
  queued: WorkQueueItem | null;
  reviewers: WorkQueueResponse["reviewers"];
}) {
  const assigned =
    queued?.assigned_reviewer ??
    (view.assigned_reviewer_id
      ? (reviewers.find((r) => r.user_id === view.assigned_reviewer_id) ?? null)
      : null);
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/tex/queue"
          search={{}}
          className="text-sm text-neutral-500 hover:text-neutral-800"
        >
          ← Queue
        </Link>
        <h1 className="mt-1 flex flex-wrap items-center gap-3 font-display text-3xl font-bold text-neutral-900">
          <Mono>{view.reference}</Mono>
          <StageBadge stage={view.stage} />
          {queued && <NextActionBadge action={queued.next_action} />}
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          {view.buyer_business_name} · {view.dealer_legal_name} · requested{" "}
          <Mono>{formatAmount(view.pricing.requested_principal)}</Mono>
          {view.application && (
            <>
              {" "}
              · submitted {formatDate(
                view.application.submitted_at,
              )} (revision {view.application.revision_number})
            </>
          )}
        </p>
        {view.specialist_review_required && (
          <p className="mt-1 text-xs text-amber-800">
            Specialist review required (trade-in or other flag on this request).
          </p>
        )}
      </div>
      <div className="text-right">
        <div className="text-xs uppercase tracking-wide text-neutral-500">
          Reviewer
        </div>
        <AssignControl
          me={me}
          caseId={view.id}
          assigned={
            assigned
              ? {
                  user_id: assigned.user_id,
                  name: assigned.name,
                  email: assigned.email,
                  tex_role: assigned.tex_role,
                }
              : null
          }
          reviewers={reviewers}
        />
      </div>
    </div>
  );
}

// ── application tab: the borrower's submitted revision, read-only ──

function addressText(a: BuyerAddressView | null | undefined): string | null {
  if (!a) return null;
  return [a.line1, a.line2, `${a.city}, ${a.state} ${a.postal_code}`]
    .filter(Boolean)
    .join(", ");
}

function FactRow({
  label,
  fact,
}: {
  label: string;
  fact: FinancialFact | undefined;
}) {
  if (!fact) return <Row label={label} value={null} />;
  if (fact.status === "unknown")
    return (
      <Row
        label={label}
        value={
          <span className="text-neutral-500">
            Unknown (borrower did not know)
          </span>
        }
      />
    );
  return (
    <Row
      label={label}
      value={
        <>
          <Mono>{formatAmount(fact.amount)}</Mono>
          <span className="text-xs text-neutral-500">
            {" "}
            · {FINANCIAL_PERIOD_LABELS[fact.period]}
            {fact.period_end && ` to ${formatDay(fact.period_end)}`} ·{" "}
            {FINANCIAL_SOURCE_LABELS[fact.source]}
          </span>
        </>
      }
    />
  );
}

function ApplicationTab({ view }: { view: TexCaseView }) {
  const app = view.application;
  if (!app) {
    return (
      <Alert tone="info">
        The borrower has not submitted an application yet. Tex sees answers only
        once they are frozen as a revision.
      </Alert>
    );
  }
  const steps: ApplicationDraftSteps = app.payload;
  const b = steps.business;
  const o = steps.owners;
  const f = steps.financials;
  const e = steps.equipment_review;
  return (
    <div className="space-y-6">
      <Alert tone="info">
        Revision {app.revision_number}, submitted {formatDate(app.submitted_at)}
        . Frozen at submit; a correction from the borrower becomes a new
        revision. Payload <Mono>{app.payload_sha256.slice(0, 12)}…</Mono> ·
        schema <Mono>{app.schema_version}</Mono>
      </Alert>

      {app.discrepancies.length > 0 && (
        <Card title="Discrepancies between the dealer request and the borrower's answers">
          <DiscrepancyList items={app.discrepancies} />
        </Card>
      )}

      <Card title="Business">
        {b ? (
          <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            <dl>
              <Row label="Legal name" value={b.legal_name} />
              <Row label="Trading name" value={b.trading_name} />
              <Row
                label="Entity type"
                value={ENTITY_TYPE_LABELS[b.entity_type]}
              />
              <Row label="Formed" value={formatDay(b.formed_on)} />
              <Row
                label="Operating since"
                value={b.operating_since ? formatDay(b.operating_since) : null}
              />
              <Row label="Industry" value={b.industry} />
            </dl>
            <dl>
              <Row
                label="Business address"
                value={addressText(b.business_address)}
              />
              <Row
                label="Equipment use address"
                value={
                  addressText(b.equipment_use_address) ?? "Same as business"
                }
              />
              <Row
                label="Representative"
                value={`${b.representative.name}, ${b.representative.title}`}
              />
              <Row
                label="Email"
                value={<Mono>{b.representative.email}</Mono>}
              />
              <Row
                label="Phone"
                value={<Mono>{b.representative.phone}</Mono>}
              />
            </dl>
          </div>
        ) : (
          <p className="text-sm text-neutral-500">Not provided.</p>
        )}
      </Card>

      <Card title="Owners">
        {o && o.owners.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="text-left font-display text-[11px] font-bold uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="py-1 pr-3">Name</th>
                <th className="py-1 pr-3">Title</th>
                <th className="py-1 pr-3 text-right">Ownership</th>
                <th className="py-1 pr-3">Contact</th>
                <th className="py-1">Address</th>
              </tr>
            </thead>
            <tbody>
              {o.owners.map((own, i) => (
                <tr key={i} className="border-t border-neutral-100 align-top transition-colors hover:bg-neutral-50/60">
                  <td className="py-2 pr-3 font-medium">{own.full_name}</td>
                  <td className="py-2 pr-3">{own.title}</td>
                  <td className="py-2 pr-3 text-right">
                    <Mono>{own.ownership_percent}%</Mono>
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    <Mono>{own.email}</Mono>
                    {own.phone && (
                      <>
                        <br />
                        <Mono>{own.phone}</Mono>
                      </>
                    )}
                  </td>
                  <td className="py-2 text-xs text-neutral-500">
                    {addressText(own.address) ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-neutral-500">Not provided.</p>
        )}
      </Card>

      <Card title="Financials (as stated by the borrower)">
        {f ? (
          <dl>
            <Row
              label="Amount requested"
              value={<Mono>{formatAmount(f.requested_amount)}</Mono>}
            />
            <Row label="Intended use" value={f.intended_use} />
            <Row label="Repayment source" value={f.repayment_source} />
            <FactRow label="Annual revenue" fact={f.annual_revenue} />
            <FactRow
              label="Monthly debt service"
              fact={f.monthly_debt_service}
            />
          </dl>
        ) : (
          <p className="text-sm text-neutral-500">Not provided.</p>
        )}
        <p className="mt-3 text-xs text-neutral-500">
          Stated figures are evidence level “stated” until a document verifies
          them. Record what you verify under Review → Facts.
        </p>
      </Card>

      <Card title="Borrower's view of the quote">
        {e ? (
          <dl>
            <Row
              label="Quote matches"
              value={
                e.quote_matches
                  ? "Yes — borrower confirmed the equipment and price"
                  : "No — borrower disputed the quote"
              }
            />
            <Row label="Note" value={e.note} />
            {e.item_flags.length > 0 && (
              <Row
                label="Item flags"
                value={
                  <ul className="list-disc pl-4">
                    {e.item_flags.map((fl) => (
                      <li key={fl.equipment_item_id}>
                        <Mono>
                          {view.equipment.find(
                            (it) => it.id === fl.equipment_item_id,
                          )?.serial_number ?? fl.equipment_item_id}
                        </Mono>
                        : {fl.note}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
          </dl>
        ) : (
          <p className="text-sm text-neutral-500">Not provided.</p>
        )}
      </Card>
    </div>
  );
}

// ── assets tab: dealer's equipment, invoice math, eligibility ──

function AssetsTab({ view }: { view: TexCaseView }) {
  const p = view.pricing;
  return (
    <div className="space-y-6">
      <Card title="Equipment (dealer request)">
        <table className="w-full text-sm">
          <thead className="text-left font-display text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="py-1 pr-4">Unit</th>
              <th className="py-1 pr-4">Condition</th>
              <th className="py-1 pr-4">Serial</th>
              <th className="py-1 pr-4">Title</th>
              <th className="py-1 pr-4 text-right">Qty</th>
              <th className="py-1 text-right">Line total</th>
            </tr>
          </thead>
          <tbody>
            {view.equipment.map((e) => (
              <tr key={e.id} className="border-t border-neutral-100 align-top transition-colors hover:bg-neutral-50/60">
                <td className="py-2 pr-4">
                  <div className="font-medium">
                    {[e.model_year, e.make, e.model]
                      .filter(Boolean)
                      .join(" ") || e.category}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {e.category}
                    {e.hours_or_miles !== null && (
                      <> · {e.hours_or_miles.toLocaleString()} hrs/mi</>
                    )}
                    {e.existing_lien_noted && <> · existing lien noted</>}
                  </div>
                  {e.description && (
                    <div className="mt-1 text-xs text-neutral-500">
                      {e.description}
                    </div>
                  )}
                </td>
                <td className="py-2 pr-4">{humanize(e.condition)}</td>
                <td className="py-2 pr-4">
                  {e.serial_number ? <Mono>{e.serial_number}</Mono> : "—"}
                </td>
                <td className="py-2 pr-4 text-xs">
                  {e.title_kind
                    ? `${humanize(e.title_kind)}${e.vehicle_class ? ` (${e.vehicle_class})` : ""}`
                    : "—"}
                </td>
                <td className="py-2 pr-4 text-right">
                  <Mono>{e.quantity}</Mono>
                </td>
                <td className="py-2 text-right">
                  <Mono>{formatAmount(e.line_total)}</Mono>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="Invoice and amount requested">
        <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
          <dl>
            <Row
              label="Equipment total"
              value={<Mono>{formatAmount(p.equipment_total)}</Mono>}
            />
            <Row
              label="Financed soft costs"
              value={<Mono>{formatAmount(p.financed_soft_costs)}</Mono>}
            />
            <Row
              label="Discount"
              value={<Mono>−{formatAmount(p.discount)}</Mono>}
            />
            <Row
              label="Invoice total"
              value={<Mono>{formatAmount(p.invoice_total)}</Mono>}
            />
            <Row
              label="Cash contribution"
              value={<Mono>−{formatAmount(p.cash_contribution)}</Mono>}
            />
            <Row
              label="Requested principal"
              value={
                <Mono className="font-semibold">
                  {formatAmount(p.requested_principal)}
                </Mono>
              }
            />
          </dl>
          <dl>
            <Row
              label="Trade-in noted"
              value={p.trade_in_noted ? "Yes — specialist review" : "No"}
            />
            <Row
              label="Reconciliation reason"
              value={p.reconciliation_reason}
            />
            <Row
              label="Expected delivery"
              value={<Mono>{formatDay(view.delivery.expected_on)}</Mono>}
            />
            <Row
              label="Payment before delivery"
              value={
                view.delivery.pre_delivery_payment_requested
                  ? "Requested"
                  : "No"
              }
            />
            <Row label="Equipment use state" value={view.equipment_use_state} />
            <Row label="Buyer state" value={view.buyer_state} />
          </dl>
        </div>
        {view.soft_costs.length > 0 && (
          <ul className="mt-3 text-xs text-neutral-500">
            {view.soft_costs.map((s) => (
              <li key={s.id}>
                {SOFT_COST_LABELS[s.kind]}
                {s.label ? ` — ${s.label}` : ""}:{" "}
                <Mono>{formatAmount(s.amount)}</Mono>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <EligibilityCard caseId={view.id} />
    </div>
  );
}
